import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { Tabs, Col, Row, Input, Button, Popconfirm, notification, Modal } from 'antd';

import { CloseOutlined } from '@ant-design/icons';

// css
import { css } from 'emotion';
import colors from '../../../style/colors';

// actions

import TopBar from '../../ui/TopBar';
import Page from '../../ui/Page';
import { useSelector } from 'react-redux';
import getBackButtonUrl from '../../../utilities/get-back-button-url';
import getPageFromId from '../../../utilities/get-page-from-id';
import ActionWrapper from '../../ui/ActionWrapper';
import pageNavigator from '../../../utilities/page-navigator';
import { ArrowBackIcon } from 'mdi-react';
import ScrollView from '../../ui/ScrollView';
import TextInput from '../../ui/TextInput';
import InlineSpinner from '../../ui/InlineSpinner';
import ForemanTeamUser from './ForemanTeamUser';
import req from '../../../utilities/request-utility';

function ForemanRegistration(props) {
	const pages = useSelector(s => s.pages.pages);
	const lang = useSelector(s => s.language.language);

	const page = getPageFromId(pages, props.match.params.pageId);
	const backButtonURL = getBackButtonUrl(page);

	const [interfaceIsBusy, setInterfaceIsBusy] = useState(false);
	const [filterTerm, setFilterTerm] = useState('');

	const { user: loggedInUserSelector } = useSelector(({ auth }) => ({ user: auth.user }));
	const { loggedUserAuthorizedForeman = false, id: loggedUserId } = loggedInUserSelector;
	//convert it to useState
	const [groupName, setGroupName] = useState('');

	const [activeKey, setActiveKey] = useState(null);
	const [groupTabItems, setGroupTabItems] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleAddNewGroup = async () => {
		try {
			const response = await req().post(`semcotime/foremen-teams/groups`, { groupName });
			const { newGroupId } = response.data;
			refetchGroups();

			setGroupName('');
			setActiveKey(newGroupId);
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const handleDeleteGroup = async id => {
		try {
			await req().delete(`semcotime/foremen-teams/groups/${id}`);
			notification.success({
				duration: 1,
				message: 'SUCCESS',
				description: 'Group deleted successfully'
			});
			setActiveKey(null);
			refetchGroups();
			refetchMemberGroups();
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const handleUpdateGroup = async () => {
		try {
			await req().put(`semcotime/foremen-teams/groups/${activeKey}`, { groupName });
			notification.success({
				duration: 1,
				message: 'SUCCESS',
				description: 'Group updated successfully'
			});
			setGroupName('');
			refetchGroups();
			refetchMemberGroups();
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const handleAddMemberToGroup = async ({ groupId, userId, externalId }) => {
		try {
			await req().post(`semcotime/foremen-teams/groups/members`, {
				groupId,
				blueCollarId: userId,
				externalId: externalId
			});
			notification.success({
				duration: 1,
				message: 'SUCCESS',
				description: 'Member added to group successfully'
			});
			refetchMemberGroups();
		} catch (err) {
			notification.error({
				duration: 1,
				message: 'ERROR',
				description: err.response.data.message
			});
		}
	};

	const onEdit = (targetKey, action) => {
		if (action === 'add') {
			handleAddNewGroup();
		} else {
			alert('remove?');
		}
	};

	const {
		data: { data: dataUsers = [] } = {},
		isError: isErrorUsers,
		isLoading: isLoadingUsers
	} = useQuery('users', () => req()(`semcotime/foremen-teams/users`));

	const {
		data: { data: dataTeamUsers = [] } = {},
		isError: isErrorTeamUsers,
		isLoading: isLoadingTeamUsers,
		refetch: refetchTeamUsers
	} = useQuery('teamUsers', () => req()(`semcotime/foremen-teams/my-team`), {
		enabled: !loggedUserAuthorizedForeman
	});

	const {
		data: { data: dataGroups = [] } = {},
		isError: isErrorGroups,
		isLoading: isLoadingGroups,
		refetch: refetchGroups
	} = useQuery('foremanGroups', () => req()(`semcotime/foremen-teams/groups`), {
		enabled: loggedUserAuthorizedForeman
	});

	const {
		data: { data: dataMemberGroups = [] } = {},
		isError: isErrorMemberGroups,
		isLoading: isLoadingMemberGroups,
		isFetching: isFetchingMemberGroups,
		refetch: refetchMemberGroups
	} = useQuery('foremanMemberGroups', () => req()(`semcotime/foremen-teams/groups/members`), {
		enabled: loggedUserAuthorizedForeman
	});

	const users = {
		data: dataUsers.filter(d => d.id !== loggedInUserSelector.id),
		tempData: dataUsers,
		isError: isErrorUsers,
		isLoading: isLoadingUsers
	};

	const teamUsers = {
		data: dataTeamUsers.filter(d => d.id !== loggedInUserSelector.id),
		isError: isErrorTeamUsers,
		isLoading: isLoadingTeamUsers
	};

	const teamUserIds = dataTeamUsers.map(d => d.id);

	const addToTeam = async id => {
		setInterfaceIsBusy(true);

		try {
			await req().put(`semcotime/foremen-teams/my-team/${id}`);
			await refetchTeamUsers();
			setInterfaceIsBusy(false);
		} catch (error) {
			setInterfaceIsBusy(false);
		}
	};

	const removeFromTeam = async id => {
		setInterfaceIsBusy(true);

		try {
			await req().delete(`semcotime/foremen-teams/my-team/${id}`);
			await refetchTeamUsers();
			setInterfaceIsBusy(false);
		} catch (error) {
			setInterfaceIsBusy(false);
		}
	};

	const removeFromGroup = async id => {
		setInterfaceIsBusy(true);
		try {
			await req().delete(`semcotime/foremen-teams/groups/members/${id}`);
			notification.success({
				duration: 1,
				message: 'SUCCESS',
				description: 'Member removed from group successfully'
			});
			await refetchMemberGroups();
			setInterfaceIsBusy(false);
		} catch (error) {
			setInterfaceIsBusy(false);
		}
	};

	useEffect(() => {
		const loggedInUser = users.tempData.filter(d => d.id === loggedInUserSelector.id);
		if (loggedInUser.length !== 0) addToTeam(loggedInUser[0].id);
	}, [users.data.length]);

	useEffect(() => {
		if (loggedUserAuthorizedForeman) {
			const items = dataGroups.map((item, index) => {
				let localmembers = [];

				localmembers = dataMemberGroups.filter(
					lmember => lmember.groupId === item.id && lmember.blueCollarId !== loggedUserId
				);

				const membersCount = localmembers.length;
				const childre = localmembers.map(lmembe => (
					<ForemanTeamUser
						key={lmembe.id}
						onClick={() => removeFromGroup(lmembe.id)}
						user={lmembe}
						isDeletable={true}
					/>
				));
				return {
					label: (
						<span
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								padding: '4px 8px',
								fontSize: '14px',
								fontWeight: '500',
								color: '#333'
							}}>
							{item.groupName}
							<Popconfirm
								placement="topRight"
								title={'Delete Group? All members will be removed.'}
								okText="Yes"
								onConfirm={() => handleDeleteGroup(item.id)}
								cancelText="No">
								<Button
									type="text"
									size="small"
									style={{
										marginLeft: '18px',
										marginRight: '-18px',
										padding: 0,
										fontSize: '16px',
										color: '#ff4d4f',
										border: 'none',
										background: 'transparent'
									}}
									icon={<CloseOutlined />}
								/>
							</Popconfirm>
						</span>
					),
					children: (
						<>
							{isLoadingMemberGroups && (
								<Col span={24}>
									<InlineSpinner style={{ margin: '2rem' }} />
								</Col>
							)}

							{!isLoadingMemberGroups && membersCount === 0 && (
								<Col span={24}>
									<div className="meta">No members found</div>
								</Col>
							)}

							{!isLoadingMemberGroups && membersCount > 0 && childre}
						</>
					),

					key: item.id
				};
			});

			setGroupTabItems(items);

			if (!activeKey && dataGroups.length > 0) {
				setActiveKey(dataGroups[0].id);
			}
		}
	}, [dataMemberGroups, dataGroups]);

	return (
		<Page className={componentStyles(props)}>
			<TopBar
				actionLeft={
					<ActionWrapper onClick={() => pageNavigator(backButtonURL, 'backward')}>
						<ArrowBackIcon />
					</ActionWrapper>
				}
				title={page.title}
			/>
			<div className="main-content">
				<div className="col col-left">
					<TextInput
						value={filterTerm}
						onChange={e => setFilterTerm(e.target.value)}
						placeholder={lang.filterList}
						style={{ margin: '1rem 1rem 0 1rem', width: 'calc(100% - 2rem)' }}
					/>

					<ScrollView style={{ padding: '1rem' }}>
						{users.isLoading && !users.isError && <InlineSpinner style={{ margin: '2rem' }} />}

						{!users.isLoading &&
							users.data.map(user => {
								if (filterTerm) {
									const regex = new RegExp(
										`(${filterTerm})|(${filterTerm.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')})`,
										'gi'
									);
									if (
										!regex.test(user.name) &&
										!regex.test(user.ticketNumber) &&
										!regex.test(user.externalId)
									)
										return null;
								}

								let isSelected = false;

								if (loggedUserAuthorizedForeman) {
									isSelected = dataMemberGroups.some(member => member.blueCollarId === user.id);
								} else isSelected = teamUserIds.includes(user.id);

								return (
									<ForemanTeamUser
										key={user.id}
										user={user}
										onClick={() => {
											if (loggedUserAuthorizedForeman) {
												if (dataGroups.length === 0)
													return notification.error({
														duration: 1.5,
														message: 'Error Member Add',
														description: 'Please create a group first'
													});

												if (!isSelected) {
													handleAddMemberToGroup({
														externalId: user.externalId,
														groupId: activeKey,
														userId: user.id
													});
												}
											} else {
												console.log('isSelected: ', isSelected);

												if (!isSelected) return addToTeam(user.id);
											}
										}}
										isSelected={isSelected}
									/>
								);
							})}
					</ScrollView>
				</div>

				<div className="col col-right">
					<ScrollView style={{ padding: '1rem' }}>
						{!loggedUserAuthorizedForeman && (
							<>
								<div className="meta" style={{ margin: '0.75rem 0 1.7rem 0', textAlign: 'left' }}>
									{teamUsers.data.length} / {users.data.length}
									{(users.isLoading || teamUsers.isLoading || interfaceIsBusy) && (
										<InlineSpinner
											size="14"
											style={{ display: 'inline-block', margin: '0 0rem -0.6rem 0.5rem' }}
										/>
									)}
								</div>

								{teamUsers.isLoading && !teamUsers.isError && (
									<InlineSpinner style={{ margin: '2rem' }} />
								)}

								{!teamUsers.isLoading &&
									teamUsers.data.map(user => (
										<ForemanTeamUser
											key={user.id}
											onClick={() => removeFromTeam(user.id)}
											user={user}
											isDeletable={true}
										/>
									))}
							</>
						)}
						{loggedUserAuthorizedForeman && (
							<Row gutter={[16, 16]}>
								<Col span={24}>
									<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
										<Button
											size="large"
											type="primary"
											loading={isLoadingGroups}
											style={{ marginLeft: '1rem' }}
											onClick={() => {
												setActiveKey(null);
												setGroupName('');
												setIsModalOpen(true);
											}}>
											Add Group
										</Button>
										{activeKey && (
											<Button
												size="large"
												type="primary"
												loading={isLoadingGroups}
												style={{ marginLeft: '1rem' }}
												onClick={() => setIsModalOpen(true)}>
												Edit Group Name
											</Button>
										)}
									</div>
								</Col>

								{!isLoadingGroups && !isErrorGroups && dataGroups.length === 0 && (
									<Col span={24}>
										<div className="meta">No groups found</div>
									</Col>
								)}

								<Col span={24}>
									<Tabs
										hideAdd
										type="card"
										onChange={e => setActiveKey(e)}
										activeKey={activeKey}
										onEdit={onEdit}
										items={groupTabItems}
									/>
								</Col>
							</Row>
						)}
					</ScrollView>
				</div>
			</div>
			<Modal open={isModalOpen} footer={null} onCancel={() => setIsModalOpen(false)} centered width={400}>
				<div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '1rem' }}>Enter group name</div>
				<Input
					onChange={e => setGroupName(e.target.value)}
					type="text"
					size="large"
					style={{ width: '100%' }}
					placeholder={'Enter group name'}
					disabled={isLoadingGroups}
					value={groupName}
				/>
				<Button
					size="large"
					type="primary"
					loading={isLoadingGroups}
					style={{ marginTop: '1rem', width: '100%' }}
					onClick={() => {
						try {
							if (activeKey) handleUpdateGroup();
							if (!activeKey) handleAddNewGroup();

							setIsModalOpen(false);
						} catch (error) {
							console.log('error: ', error);
							setIsModalOpen(false);
						}
					}}>
					{activeKey ? 'Update' : 'Add'}
				</Button>
			</Modal>
		</Page>
	);
}

const componentStyles = () => css`
	.main-content {
		display: flex;
		justify-content: space-between;
		height: 100%;
		overflow: hidden;

		.col {
			flex: 50% 0 1;
			background-color: ${colors.white};
		}

		.col-left {
			border-right: 1px ${colors.midGrey} solid;
			box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.1);
			position: relative;
			display: flex;
			flex-direction: column;
		}

		.col-right {
			display: flex;
			flex-direction: column;
			width: 50%;
		}
	}

	.meta {
		color: ${colors.darkGrey};
		text-align: center;
	}
`;

export default ForemanRegistration;
